import { z } from 'zod';

/**
 * Role hierarchy. `superadmin` is platform-wide (org_id is null); every other role
 * is scoped to exactly one organization.
 */
export const ROLES = [
  'superadmin',
  'org_admin',
  'org_engineer',
  'org_operator',
  'org_viewer',
  'org_technician',
] as const;

export const roleSchema = z.enum(ROLES);
export type Role = z.infer<typeof roleSchema>;

/**
 * Higher number = more authority. Used by `hasRole` / route guards.
 *
 * ## Why `org_engineer` is 25, between operator and admin
 *
 * `hasRole` is an ORDER, not a set of labels: a role's capabilities are every
 * capability whose minimum rank it clears. So where a rung sits is the whole
 * decision, and there were three candidate positions for the field/NOC
 * engineer.
 *
 * - BELOW operator would be a lie: the engineer authorises, reboots and
 *   reconfigures ONUs, which an operator can already do to a CPE. A rung that
 *   sits under one it outranks in practice makes `hasRole` useless.
 * - ABOVE admin would hand the engineer users, keys, billing and settings by
 *   construction — the exact four things the role exists to withhold — and
 *   would silently widen every `org_admin` guard.
 * - BETWEEN them (25) is the only position where both halves of the intent
 *   hold at once: everything an operator may do, plus the fleet work that used
 *   to be `olt.manage`, and nothing that administers the organization.
 *
 * The gap from 20 to 30 was left wide when the ladder was written; 25 uses it
 * without renumbering anything, so no stored role and no existing comparison
 * changes meaning.
 *
 * The consequence to keep in mind: a capability an engineer must NOT hold must
 * have a minimum of `org_admin` or higher. That is why the fleet work was
 * carved OUT of `olt.manage` rather than `olt.manage` being lowered — lowering
 * it would have handed the engineer chassis deletion and stored credentials
 * along with the reboots.
 *
 * ## Why `org_technician` is 5, BELOW the viewer — and why it needs grants
 *
 * The technician is the low-level field operative: somebody sent to a
 * subscriber's wall with a phone, who has to open the job assigned to them,
 * confirm the unit is up, set the customer's WiFi, photograph and locate the
 * install, and mark it done. Nothing else — not a reboot, not a claim, not a
 * parameter edit on the CPE, and not a look at the rest of the fleet.
 *
 * It first landed at 15, between viewer and operator, on the reasoning that a
 * person who writes to a subscriber's WiFi should not sit under the read-only
 * rung. That reasoning got the READS wrong. The viewer's rung is "read-only
 * across ONUs, OLTs and gateways" — the whole fleet, every subscriber, every
 * chassis — and inheriting it handed a person sent to one wall the list of
 * every wall in the organization. The operator's rule is the narrower one:
 * a technician sees only the customers dispatch has sent them to. So the rung
 * moved to 5, below the viewer, where it inherits NOTHING from the ladder:
 *
 * - `device.read`, `site.read` and `olt.read` are the viewer's and stay
 *   there. A technician may not browse the fleet.
 * - What a technician may read is `onu.read_assigned` — the ONUs that are
 *   the target of a deployment job assigned to them, still open or closed in
 *   the last fortnight — and that is a narrowing of `olt.read`, not a rung
 *   above it. An admin who wants a senior tech to see more ticks `olt.read`
 *   in the Technician column on the Roles page.
 *
 * With nothing inherited, EVERYTHING the technician holds comes through
 * `ROLE_GRANTS`. The ladder is a TOTAL ORDER: the two acts the role exists
 * for — `field.deploy` and `device.wifi` — sit at the engineer's minimum by
 * design, and lowering either would hand it to the operator at 20 in passing,
 * which the frozen `LEGACY_ROLE_CAPABILITIES` replay would (rightly) refuse:
 * the operator never held it. `onu.read_assigned` goes one step further: it
 * has NO rung at all (`CAPABILITY_MIN_ROLE` null). Everyone above the
 * technician holds `olt.read` outright, and a second, weaker tick beside a
 * name that already reaches strictly more would change what every one of
 * those roles answers for without changing what any of them can do — so the
 * name is the technician's alone, and a holder of `olt.read` REACHES it (see
 * `CAPABILITY_NARROWS`) rather than holding it. `onu.place` — record which
 * zone, splitter and port the unit in front of them hangs off — has the same
 * shape against `topology.manage`. `onu.speedtest` — measure the line they
 * just lit — sits at the engineer's minimum like the WiFi write does. So the
 * technician reaches all five through
 * `ROLE_GRANTS` — a per-role list of capabilities granted on top of the
 * ladder, for exactly the rungs that sit below a capability's minimum on
 * purpose, or below every rung. `can()` answers yes when either the rank
 * clears the minimum OR the role's grants name the capability, and
 * `capabilitiesFor()` follows.
 *
 * 5 rather than 0 or 1 so the gap below is as usable as the one 15 sat in;
 * nothing stored and no comparison changes meaning, because no code compares
 * against the technician's rung by number.
 */
const RANK: Record<Role, number> = {
  org_technician: 5,
  org_viewer: 10,
  org_operator: 20,
  org_engineer: 25,
  org_admin: 30,
  superadmin: 100,
};

export function rankOf(role: Role): number {
  return RANK[role];
}

/** True when `role` is at least as powerful as `minimum`. */
export function hasRole(role: Role, minimum: Role): boolean {
  return RANK[role] >= RANK[minimum];
}

export const ROLE_LABELS: Record<Role, string> = {
  superadmin: 'Super admin',
  org_admin: 'Admin',
  org_engineer: 'Engineer',
  org_operator: 'Operator',
  org_technician: 'Technician',
  org_viewer: 'Viewer',
};

/** One line per role, for the Users and Roles pages. */
export const ROLE_DESCRIPTIONS: Record<Role, string> = {
  superadmin: 'The whole platform, every organization, the infrastructure underneath.',
  org_admin: 'Runs the organization: people, roles, keys, billing, settings and the chassis.',
  org_engineer:
    'Works the fleet: authorise, resync, reboot and configure ONUs, run bulk actions, resolve drift and place topology. No settings, keys, users or billing.',
  org_operator: 'Day-to-day CPE work: claim, edit and act on a subscriber’s device.',
  org_technician:
    'Field work from the phone: open the job assigned to you, confirm the subscriber is up, set their WiFi, record which splitter and port they hang off, photograph and locate the install, mark it done. Sees only the customers on jobs assigned to them. Nothing else.',
  org_viewer: 'Read-only across ONUs, OLTs and gateways.',
};

/**
 * Capabilities, so the UI and the API agree on who may do what without
 * scattering role comparisons through either side.
 *
 * ## The 2026-08-30 refresh
 *
 * The original fourteen were written before bulk actions, drift, firmware
 * campaigns, topology/cascade, chassis backup and restore, billing, the status
 * embed and universal search existed. Everything the platform grew since then
 * landed on whichever of the fourteen was nearest, and `olt.manage` collected
 * most of it — ninety route guards spanning a VLAN detect (a read that happens
 * to open an SSH session) and a fleet-wide bulk recreate (which rewrites
 * thousands of live subscribers). One name for both is not a permission, it is
 * a coin toss.
 *
 * Eleven capabilities were carved out. Every one of them is ADDITIVE: no name
 * was removed or renamed, and each new name records where it came from in
 * `CAPABILITY_ORIGIN`. Because a carved capability's minimum role is never
 * higher than its origin's, every pre-existing role reaches exactly the routes
 * it reached before — see `LEGACY_ROLE_CAPABILITIES`, which freezes the old
 * answer, and the test that replays it through the origin map.
 *
 * Order matters here: it is the row order of the Roles matrix, so related
 * capabilities are kept adjacent.
 */
export const CAPABILITIES = [
  'org.manage', // create/edit organizations (platform level)
  'user.manage', // invite/edit users inside an org, and edit the role matrix
  'device.read',
  'device.claim', // assign/unassign a device to an org
  'device.write', // set parameters, rename
  'device.wifi', // set a subscriber's WiFi name and password — the one CPE write a technician makes
  'device.action', // reboot, factory reset, summon — one CPE at a time
  'firmware.manage', // firmware images in use, push and campaigns across many CPEs
  'device.delete',
  'site.read',
  'topology.manage', // splitters, ODF, zones, cascades — where the fibre physically goes
  'onu.place', // record where ONE subscriber's ONU sits — zone, splitter, port — the technician's slice of topology.manage
  'field.deploy', // work a deployment job: start it, capture the fix, submit it
  'field.manage', // raise deployment jobs and assign them to whoever holds field.deploy
  'field.close_incomplete', // confirm and close a job the field could not finish
  'site.manage', // create gateways, rotate keys, revoke certs
  'olt.read',
  'onu.read_assigned', // the ONUs on deployment jobs assigned to you — the technician's window onto the fleet
  'olt.diagnose', // ask a chassis about itself: probe, health, sync, SNMP walk, VLAN detect
  'onu.provision', // authorise/deauthorise/resync/reboot/configure ONUs on the chassis
  'onu.speedtest', // run a TR-143 speed test on a subscriber's line, or file a phone test against it
  'bulk.execute', // plan and run a fleet-wide bulk action
  'drift.resolve', // apply or dismiss a drift finding, reconcile a report
  'olt.manage', // the chassis itself: onboard, edit, delete, credentials, console, config
  'olt.backup', // take and remove chassis configuration backups
  'olt.restore', // push a backup back onto a chassis
  'settings.manage',
  'apikey.manage', // mint, roll and revoke API keys
  'branding.manage', // logo, colours, custom domain, public status embed
  'billing.manage', // pay an invoice, check a payment
  'infra.manage', // platform infrastructure: GenieACS, tunnels, deploy (superadmin)
  'audit.read',
] as const;

export type Capability = (typeof CAPABILITIES)[number];

/**
 * The fourteen capabilities as they stood before the 2026-08-30 refresh.
 *
 * Frozen on purpose. It is the "before" half of the proof that the split
 * changed nobody's access, and freezing it as a literal rather than deriving it
 * means a future edit to `CAPABILITIES` cannot quietly move the goalposts.
 */
export const LEGACY_CAPABILITIES = [
  'org.manage',
  'user.manage',
  'device.read',
  'device.claim',
  'device.write',
  'device.action',
  'device.delete',
  'site.read',
  'site.manage',
  'olt.read',
  'olt.manage',
  'settings.manage',
  'infra.manage',
  'audit.read',
] as const;

export type LegacyCapability = (typeof LEGACY_CAPABILITIES)[number];

/**
 * Where each capability came from — an original maps to itself, a carved one to
 * the capability it was carved out of.
 *
 * This is the mapping the migration replays over stored rows (`org_role_
 * capabilities` overrides and `custom` API keys both hold literal capability
 * lists), and the mapping the behaviour-preservation test replays over the
 * built-in defaults. Both questions are "what did holding X used to reach", so
 * both read one table rather than two that can disagree.
 */
export const CAPABILITY_ORIGIN: Record<Capability, LegacyCapability> = {
  'org.manage': 'org.manage',
  'user.manage': 'user.manage',
  'device.read': 'device.read',
  'device.claim': 'device.claim',
  'device.write': 'device.write',
  /*
    `olt.manage`, not `device.write` — and the choice was forced by what this
    table MEANS. The origin is "what did holding X used to reach", and the
    route `device.wifi` gates (`POST /onus/:id/wifi`) is the desired-WiFi
    mechanism of `POST /olts/:id/onus/:onuId/wifi/desired`: store the SSID
    against the ONU, push it if the unit informs, queue it if not. That door has
    only ever been `onu.provision`, itself carved from `olt.manage`. An operator
    holding `device.write` sets WiFi on an INFORMING device through the ACS
    card, and never could set it ahead of the inform — so an origin of
    `device.write` would make the per-route replay claim the operator lost a
    door they never had, and setting the minimum low enough to satisfy it would
    hand them that door. Carved out 2026-09-11 so a rung that must hold NEITHER
    `onu.provision` nor `device.write` — the technician, who sets one
    subscriber's WiFi on install day and edits nothing else — can be handed
    exactly this.
  */
  'device.wifi': 'olt.manage',
  'device.action': 'device.action',
  'firmware.manage': 'device.action',
  'device.delete': 'device.delete',
  'site.read': 'site.read',
  'topology.manage': 'site.manage',
  /*
    `site.manage`, because an origin is one of the frozen fourteen and
    `topology.manage` — the name this one actually narrows — is itself carved
    from `site.manage`. The two questions this table answers stay honest:
    "what did holding X used to reach" is the placement write, which was only
    ever `site.manage` and then `topology.manage`; and the replay finds every
    role that held `site.manage` reaching it still. The narrowing relation is
    recorded where narrowings live, in `CAPABILITY_NARROWS`, not here. Carved
    2026-09-12 so the technician — who holds no `topology.manage` and must not
    (splitters, ODF, zones and cascades are the whole outside plant) — can be
    handed the one write install day needs: recording which zone, splitter and
    port the unit in front of them hangs off, on an ONU inside their own jobs.
  */
  'onu.place': 'site.manage',
  /*
    All three field capabilities come from `site.manage`, for the same reason
    `topology.manage` did: a deployment job is about where equipment physically
    lands, which is the half of `site.manage` that was never about gateways,
    keys or provisioning scripts. Nothing is widened by the choice — every role
    that holds `site.manage` today already reaches everything these gate.
  */
  'field.deploy': 'site.manage',
  'field.manage': 'site.manage',
  'field.close_incomplete': 'site.manage',
  'site.manage': 'site.manage',
  'olt.read': 'olt.read',
  /*
    A NARROWING of the ONU read, not a new door. `GET /api/onus` and
    `GET /api/onus/:id` have only ever been `olt.read`; this name reaches the
    same two routes with the list cut down to the ONUs on deployment jobs
    assigned to the caller. Every role that held `olt.read` reaches strictly
    more than this gates, so the origin is honest in both directions of the
    replay: nobody who could read the fleet lost the assigned slice of it, and
    nobody who could not read the fleet gains it by holding this. Carved
    2026-09-12 for the technician rung, which moved BELOW the viewer so it no
    longer inherits `olt.read` and has to be handed the slice by name — and
    held by NO other rung (`CAPABILITY_MIN_ROLE` null): every role above
    holds `olt.read` and reaches the slice through it, see `CAPABILITY_NARROWS`.
  */
  'onu.read_assigned': 'olt.read',
  'olt.diagnose': 'olt.manage',
  'onu.provision': 'olt.manage',
  /*
    `olt.manage`, because diagnostics were. The TR-143 line test is the same
    act as the ping and traceroute the Diagnostics card already runs — arm an
    object on the CPE, wait for it to report — and those have only ever been
    reached through the chassis-side door that `olt.manage` was before the
    split. Carved 2026-09-12 so the technician rung, which holds neither
    `onu.provision` nor `device.write`, can be handed exactly this one read
    of a subscriber's line and nothing that changes it.
  */
  'onu.speedtest': 'olt.manage',
  'bulk.execute': 'olt.manage',
  'drift.resolve': 'olt.manage',
  'olt.manage': 'olt.manage',
  'olt.backup': 'olt.manage',
  'olt.restore': 'olt.manage',
  'settings.manage': 'settings.manage',
  'apikey.manage': 'settings.manage',
  'branding.manage': 'settings.manage',
  'billing.manage': 'settings.manage',
  'infra.manage': 'infra.manage',
  'audit.read': 'audit.read',
};

/**
 * The capabilities carved out of `legacy`, in canonical order.
 *
 * A stored list holding `legacy` has to gain all of these to keep reaching what
 * it reached — that is what the 0082 migration does to existing rows, and what
 * `expandLegacyCapabilities` does for anything read at runtime.
 */
export function capabilitiesCarvedFrom(legacy: LegacyCapability): Capability[] {
  return CAPABILITIES.filter((c) => c !== legacy && CAPABILITY_ORIGIN[c] === legacy);
}

/**
 * Widen a stored capability list written before the split.
 *
 * A row saved on 2026-08-29 says `olt.manage` and means "everything olt.manage
 * reached then", which is now seven names. The 0082 migration rewrites every
 * such row once; this is the same rule in TypeScript, so a test can assert the
 * SQL and the model agree rather than trusting that two hand-written lists
 * stayed in step.
 *
 * Idempotent, and it never widens beyond the origin: holding `onu.provision`
 * alone does not get you `olt.manage`.
 */
export function expandLegacyCapabilities(stored: readonly string[]): Capability[] {
  const held = new Set<string>(stored);
  for (const legacy of LEGACY_CAPABILITIES) {
    if (held.has(legacy)) for (const carved of capabilitiesCarvedFrom(legacy)) held.add(carved);
  }
  return CAPABILITIES.filter((c) => held.has(c));
}

/**
 * What an API key may never do, however it was minted.
 *
 * A credential that can create users or organizations can escalate itself out
 * of its own scope — mint a superadmin, or move its own tenant — so this holds
 * for `read`, `readwrite` and `custom` alike. It is enforced three times on
 * purpose: the create/update schemas refuse to store one, the resolver strips
 * any that somehow survived, and `requireCapability` refuses at the route.
 */
export const CAPABILITIES_NEVER_ON_A_KEY = ['user.manage', 'org.manage'] as const;

/**
 * The capabilities a key of this preset scope carries, before that filter.
 *
 * A `custom` list is taken literally and NOT widened through
 * `expandLegacyCapabilities`: the picker offers every capability by name, so a
 * minter who ticked `device.action` and left `firmware.manage` alone meant it.
 * Keys minted before the split were widened once, by the 0082 migration.
 */
export function keyCapabilities(
  scope: 'read' | 'readwrite' | 'custom',
  chosen: readonly Capability[],
  platform: boolean,
): Capability[] {
  const base: Capability[] =
    scope === 'custom'
      ? [...chosen]
      : capabilitiesFor(platform ? 'superadmin' : scope === 'readwrite' ? 'org_admin' : 'org_viewer');
  return base.filter(
    (c) => !(CAPABILITIES_NEVER_ON_A_KEY as readonly string[]).includes(c),
  );
}

/**
 * The lowest role that holds each capability, by default.
 *
 * Read this beside `CAPABILITY_ORIGIN`: a carved capability's minimum is never
 * ABOVE its origin's, which is what makes the split invisible to the roles that
 * existed before. Where it is BELOW its origin's, that is the whole point — the
 * engineer rung was cut into the gap those capabilities now sit in.
 *
 * - `firmware.manage` (operator, from device.action): a campaign is many CPEs
 *   at once rather than one, but an operator could already push firmware to
 *   each of them in turn, so lowering nothing and raising nothing is honest.
 * - `topology.manage` (engineer, from site.manage): where a fibre lands is
 *   field work. `site.manage` keeps the gateways, their keys and their
 *   provisioning scripts — the part an engineer has no business in.
 * - `olt.diagnose`, `onu.provision`, `bulk.execute`, `drift.resolve`
 *   (engineer, from olt.manage): the fleet work the role exists for.
 * - `olt.backup` / `olt.restore` (admin, from olt.manage): a restore rewrites
 *   a live chassis from a file. It stays where it was, but under its own name,
 *   so an org that WANTS its engineers to restore can grant exactly that in the
 *   matrix instead of handing over all of `olt.manage`.
 * - `apikey.manage`, `branding.manage`, `billing.manage` (admin, from
 *   settings.manage): minting a credential, changing the org's public face and
 *   spending its money were three very different acts behind one checkbox.
 * - `audit.read` stays at admin deliberately. A NOC engineer would find the OLT
 *   log useful, but who-did-what is also a surveillance surface; an org that
 *   wants its engineers to have it can tick one box on the Roles page.
 * - `field.deploy` (engineer, from site.manage): working a deployment job IS
 *   the engineer role. `field.manage` sits at admin because raising a job and
 *   pointing it at a person is dispatch, not field work.
 * - `field.close_incomplete` (admin, from site.manage) is the operator's rule
 *   made structural. Confirming a job the field could not finish is a separate
 *   act from doing the field work, and it is deliberately NOT held by the
 *   engineer rung: a threshold anyone can wave through on their own behalf is
 *   not a threshold. The route refuses it to the submitter on top of this, so
 *   an admin who went out with a phone still cannot sign off their own job.
 * - `device.wifi` (engineer, from olt.manage): an engineer already holds the
 *   act through `onu.provision` (the desired-WiFi route on the OLT), so naming
 *   it at engineer widens nobody. The OPERATOR is deliberately not widened —
 *   an operator sets WiFi the way they always have, through `device.write` on
 *   the ACS device, and never held the set-ahead door — and the technician,
 *   who sits BELOW the operator, reaches it through `ROLE_GRANTS` rather than
 *   by this minimum being lowered past the operator's rung on the way down to
 *   theirs.
 * - `onu.read_assigned` (NO rung, from olt.read): a grant-only name. Every
 *   rung from the viewer up holds `olt.read`, which reaches strictly more
 *   than this narrowing does, so there is no rung at which naming it changes
 *   what anybody can do — and a rung it was named at would change what that
 *   role ANSWERS FOR (`capabilitiesFor`, `/auth/me`, the Roles matrix, the
 *   key presets) for nothing. The technician, below all of them, reaches it
 *   through `ROLE_GRANTS`, because it is the ONLY ONU read the rung is meant
 *   to have; everyone else reaches the slice through `olt.read`
 *   (`CAPABILITY_NARROWS`), which is also how an admin who holds no such
 *   name is allowed to tick it in the Technician column.
 * - `onu.place` (NO rung, from site.manage via topology.manage): the same
 *   shape for the same reason. It reaches one write and its picker — `PATCH
 *   /api/onus/:id/placement` and `GET /api/onus/:id/placement/options`, cut
 *   to the ONUs on the caller's own jobs — and every rung from the engineer
 *   up holds `topology.manage`, which reaches both on every ONU in the
 *   organization. Naming it at the engineer's
 *   rung would widen nobody's reach and would add a name to what three roles
 *   ANSWER FOR; naming it on no rung leaves every other role's set exactly as
 *   it was, and the admin ticks it in the Technician column because holding
 *   `topology.manage` reaches it.
 * - `onu.speedtest` (engineer, from olt.manage): a speed test writes three
 *   diagnostics objects on the CPE and reads them back — a diagnostic, not a
 *   configuration change, and the engineer already runs ping and traceroute
 *   through the same door. The operator is deliberately not widened (they
 *   never held the diagnostics door), and the technician reaches it through
 *   `ROLE_GRANTS`: measuring the line at the wall on install day is the
 *   technician's job, and it changes nothing on the subscriber's unit.
 *
 * `null` is exactly that: no rung holds the name by rank, only a grant does.
 */
const CAPABILITY_MIN_ROLE: Record<Capability, Role | null> = {
  'org.manage': 'superadmin',
  'user.manage': 'org_admin',
  'device.read': 'org_viewer',
  'device.claim': 'org_operator',
  'device.write': 'org_operator',
  'device.wifi': 'org_engineer',
  'device.action': 'org_operator',
  'firmware.manage': 'org_operator',
  'device.delete': 'org_admin',
  'site.read': 'org_viewer',
  'topology.manage': 'org_engineer',
  'onu.place': null,
  'field.deploy': 'org_engineer',
  'field.manage': 'org_admin',
  'field.close_incomplete': 'org_admin',
  'site.manage': 'org_admin',
  'olt.read': 'org_viewer',
  'onu.read_assigned': null,
  'olt.diagnose': 'org_engineer',
  'onu.provision': 'org_engineer',
  'onu.speedtest': 'org_engineer',
  'bulk.execute': 'org_engineer',
  'drift.resolve': 'org_engineer',
  'olt.manage': 'org_admin',
  'olt.backup': 'org_admin',
  'olt.restore': 'org_admin',
  'settings.manage': 'org_admin',
  'apikey.manage': 'org_admin',
  'branding.manage': 'org_admin',
  'billing.manage': 'org_admin',
  'infra.manage': 'superadmin',
  'audit.read': 'org_admin',
};

/**
 * What each role held before the 2026-08-30 refresh, frozen as a literal.
 *
 * The safety net for "additive and behaviour-preserving". A test maps every
 * capability a role holds TODAY back through `CAPABILITY_ORIGIN` and asserts
 * the result is exactly this — so a split that quietly promoted or demoted a
 * route fails before it ships. `org_engineer` and `org_technician` are absent
 * because they did not exist; they are the two roles this table says nothing
 * about.
 */
export const LEGACY_ROLE_CAPABILITIES: Record<
  Exclude<Role, 'org_engineer' | 'org_technician'>,
  readonly LegacyCapability[]
> = {
  superadmin: [...LEGACY_CAPABILITIES],
  org_admin: [
    'user.manage',
    'device.read',
    'device.claim',
    'device.write',
    'device.action',
    'device.delete',
    'site.read',
    'site.manage',
    'olt.read',
    'olt.manage',
    'settings.manage',
    'audit.read',
  ],
  org_operator: [
    'device.read',
    'device.claim',
    'device.write',
    'device.action',
    'site.read',
    'olt.read',
  ],
  org_viewer: ['device.read', 'site.read', 'olt.read'],
};

/**
 * Capabilities a role holds ON TOP of the ladder.
 *
 * The ladder is a total order: a capability's minimum rung hands it to every
 * rung above as well. That is the right shape almost everywhere, and the wrong
 * shape for a rung that is meant to sit BELOW a capability's minimum on
 * purpose. The technician (5) must work a deployment job, set a subscriber's
 * WiFi and see the ONUs on the jobs it was sent to. The first two are engineer
 * (25) capabilities — lowering either minimum to 5 would hand it to the
 * viewer (10) and the operator (20) in passing, which neither ever held and
 * which the frozen legacy replay would refuse. The third has no rung at all:
 * everyone above holds `olt.read`, which already reaches more. So the grant
 * is named here, against the role, and `can()` reads it beside the rank.
 *
 * Grants are for exactly that case and nothing else. A capability a whole
 * stretch of the ladder should hold belongs in `CAPABILITY_MIN_ROLE`; a grant
 * is a hole punched in the order for one rung, and every one should be able
 * to say why the rung sits below the minimum in the first place.
 *
 * The technician's five ARE the whole role: sitting below the viewer, it
 * inherits nothing, so this list is exactly what `capabilitiesFor` answers.
 * `onu.place` (2026-09-12) is the fourth — the operator's "allow placement
 * for technician": recording where the unit in front of them sits, on an ONU
 * inside their own jobs, without `topology.manage` and the outside plant that
 * name carries. `onu.speedtest` joined the same day for the same reason the
 * WiFi write did: its minimum is the engineer rung (a diagnostic through the
 * chassis door), and a technician at the wall on install day has to be able
 * to measure the line they just lit without holding anything that changes it.
 */
const ROLE_GRANTS: Partial<Record<Role, readonly Capability[]>> = {
  org_technician: [
    'field.deploy',
    'device.wifi',
    'onu.read_assigned',
    'onu.place',
    'onu.speedtest',
  ],
};

export function can(role: Role, capability: Capability): boolean {
  const minimum = CAPABILITY_MIN_ROLE[capability];
  return (
    (minimum !== null && hasRole(role, minimum)) ||
    (ROLE_GRANTS[role]?.includes(capability) ?? false)
  );
}

/**
 * Names that are a strict NARROWING of another: the same doors, a smaller
 * slice behind them. `onu.read_assigned` reaches the two ONU reads `olt.read`
 * reaches (`GET /api/onus`, `GET /api/onus/:id`, both `requireAnyCapability`
 * of the pair), cut down to the caller's own jobs — so whoever holds the
 * wider name reaches everything the narrower one does, and more. `onu.place`
 * reaches the placement write and its picker (`PATCH /api/onus/:id/placement`,
 * `GET /api/onus/:id/placement/options`) that `topology.manage` reaches, cut
 * down the same way.
 *
 * Two readers, and both exist because the narrow names have no rung:
 *
 *  - The role editor's escalation guard ("never grant what you do not hold")
 *    would otherwise stop EVERY admin from saving the Technician column as
 *    rendered, since no admin holds `onu.read_assigned` or `onu.place` by
 *    name. Handing a role a narrowing of something the editor holds cannot
 *    take that role anywhere the editor cannot go, so `reachesCapability`
 *    counts it.
 *  - The Roles matrix's reference column, which says what a super admin
 *    reaches rather than which names sit on their rung.
 *
 * The narrowing must reach a SUBSET of the wider name's routes, by
 * construction: a route that takes the narrow name must take the wide one
 * beside it. That is pinned against the real router, for every entry here, in
 * `services/auth/__tests__/capability-split.test.ts`.
 */
export const CAPABILITY_NARROWS: Partial<Record<Capability, Capability>> = {
  'onu.read_assigned': 'olt.read',
  'onu.place': 'topology.manage',
};

/**
 * Whether a holder of `held` reaches `capability` — by name, or by holding
 * the wider name it narrows.
 */
export function reachesCapability(held: ReadonlySet<string>, capability: Capability): boolean {
  if (held.has(capability)) return true;
  const wider = CAPABILITY_NARROWS[capability];
  return wider !== undefined && held.has(wider);
}

export function capabilitiesFor(role: Role): Capability[] {
  return CAPABILITIES.filter((c) => can(role, c));
}

export const capabilitySchema = z.enum(CAPABILITIES);

/** Platform-scoped capabilities that only a superadmin ever holds; never grantable to an org role. */
export const PLATFORM_CAPABILITIES: readonly Capability[] = ['org.manage', 'infra.manage'];

/** Capabilities an org role may be granted through the editable-roles matrix. */
export const ORG_GRANTABLE_CAPABILITIES: Capability[] = CAPABILITIES.filter(
  (c) => !PLATFORM_CAPABILITIES.includes(c),
);

/**
 * How each capability reads on the Roles matrix and the API-key picker.
 *
 * In shared rather than in a page because two screens ask the same question and
 * a `Record<Capability, string>` is the only thing that makes adding a
 * capability without naming it a compile error.
 */
export const CAPABILITY_LABELS: Record<Capability, string> = {
  'org.manage': 'Manage organizations (platform)',
  'user.manage': 'Manage users and roles',
  'device.read': 'View ONUs',
  'device.claim': 'Claim / unclaim ONUs',
  'device.write': 'Edit ONU configuration',
  'device.wifi': "Set a subscriber's WiFi name and password",
  'device.action': 'Reboot, summon, reset one CPE',
  'firmware.manage': 'Firmware pushes and campaigns',
  'device.delete': 'Delete ONUs',
  'site.read': 'View gateways and topology',
  'topology.manage': 'Manage splitters, ODF, zones and cascades',
  'onu.place': "Record where a subscriber's ONU sits — zone, splitter and port",
  'field.deploy': 'Work deployment jobs in the field',
  'field.manage': 'Raise and assign deployment jobs',
  'field.close_incomplete': 'Confirm and close an incomplete deployment',
  'site.manage': 'Manage gateways & tunnels',
  'olt.read': 'View OLTs and drift',
  'onu.read_assigned': 'See the ONUs on the jobs assigned to you',
  'olt.diagnose': 'Probe, health-check and resync an OLT',
  'onu.provision': 'Authorise, resync, reboot and configure ONUs',
  'onu.speedtest': "Run a speed test on a subscriber's line",
  'bulk.execute': 'Plan and run bulk actions',
  'drift.resolve': 'Apply or dismiss drift findings',
  'olt.manage': 'Onboard, edit and delete OLTs',
  'olt.backup': 'Take and remove OLT backups',
  'olt.restore': 'Restore a backup onto an OLT',
  'settings.manage': 'Settings, ONU types & TR-069 profiles',
  'apikey.manage': 'Mint and revoke API keys',
  'branding.manage': 'Branding, custom domain & status page',
  'billing.manage': 'Pay invoices and check payments',
  'infra.manage': 'Manage platform infrastructure (GenieACS, tunnels, deploy)',
  'audit.read': 'Read the audit log',
};

/**
 * Org roles whose capabilities can be customised per org (superadmin is fixed
 * at all). In ladder order, most authority first — it is the column order of
 * the Roles matrix, so the technician is the last column, below the viewer
 * where its rank puts it.
 */
export const EDITABLE_ROLES = [
  'org_admin',
  'org_engineer',
  'org_operator',
  'org_viewer',
  'org_technician',
] as const;

/**
 * The subset an org admin may edit — never the admin role itself (no
 * self-escalation/lockout). The technician is here on purpose: a senior tech
 * an organization trusts with more (the whole fleet through `olt.read`, a
 * reboot, the outside plant through `topology.manage`) is widened by ticking
 * the box on the Roles page, not by promoting them to operator and handing
 * over every CPE write with it.
 */
export const ADMIN_EDITABLE_ROLES = [
  'org_engineer',
  'org_operator',
  'org_viewer',
  'org_technician',
] as const;

export type EditableRole = (typeof EDITABLE_ROLES)[number];

export function isEditableRole(role: Role): role is EditableRole {
  return (EDITABLE_ROLES as readonly Role[]).includes(role);
}

/** One row of the editable role→capability matrix for an org. */
export const roleMatrixRowSchema = z.object({
  role: roleSchema,
  label: z.string(),
  capabilities: z.array(capabilitySchema),
  /** True when this role uses the built-in defaults (no per-org override stored). */
  isDefault: z.boolean(),
  /** Whether the requesting caller may edit this role in this org. */
  canEdit: z.boolean(),
});
export type RoleMatrixRow = z.infer<typeof roleMatrixRowSchema>;

export const roleMatrixSchema = z.object({
  orgId: z.string(),
  orgName: z.string(),
  /** Every capability, in canonical order, for rendering the matrix rows. */
  allCapabilities: z.array(capabilitySchema),
  /** Capabilities that may be toggled on an org role (excludes platform-only). */
  grantable: z.array(capabilitySchema),
  rows: z.array(roleMatrixRowSchema),
});
export type RoleMatrix = z.infer<typeof roleMatrixSchema>;

export const setRoleCapabilitiesSchema = z.object({
  capabilities: z.array(capabilitySchema),
});
export type SetRoleCapabilities = z.infer<typeof setRoleCapabilitiesSchema>;
